import { APP_API_V1_BASE, APP_INVENTORY_API_BASE } from "../../../../config/api";
// /**
//  * TransferModal
//  *
//  * Initiates an ownership transfer for a given asset.
//  * Opened from AssetDetail.jsx when the user clicks "Transfer ownership".
//  *
//  * Props:
//  *   open        {boolean}
//  *   onClose     {() => void}
//  *   onSuccess   {(transfer, updatedAsset) => void}
//  *   asset       {object}   — the asset being transferred (must have id, serial_number,
//  *                            owner_type, ownership_state)
//  *   token       {string}
//  *   activeOrg   {object|null}
//  *
//  * API:
//  *   POST /api/v1/inventory/transfers
//  *
//  * Validation:
//  *   - transfer_type must be compatible with asset.owner_type
//  *   - to_owner_org_id must be a valid UUID
//  *   - purchase_order and invoice_number are optional but trimmed
//  */

// import React, { useState, useEffect } from "react";
// import {
//   Dialog, DialogTitle, DialogContent, DialogActions,
//   Box, Typography, Button, TextField, MenuItem,
//   Stack, Divider, Alert, CircularProgress,
//   IconButton, Chip, Stepper, Step, StepLabel,
// } from "@mui/material";
// import CloseIcon       from "@mui/icons-material/Close";
// import SwapHorizIcon   from "@mui/icons-material/SwapHoriz";
// import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import axios           from "axios";

// const API_BASE = `${APP_INVENTORY_API_BASE}`;

// // ─── Transfer type configuration ─────────────────────────────────────────────

// const TRANSFER_CONFIG = {
//   OEM_SALE: {
//     label:          "OEM Sale",
//     description:    "Transfer from OEM to a distributor or direct to customer",
//     requiredOwner:  "OEM",
//     toOwnerOptions: [
//       { value: "DISTRIBUTOR", label: "Distributor" },
//       { value: "CUSTOMER",    label: "Customer (direct)" },
//     ],
//     badgeColor: "primary",
//   },
//   DISTRIBUTOR_SALE: {
//     label:          "Distributor Sale",
//     description:    "Transfer from distributor to a reseller or direct to customer",
//     requiredOwner:  "DISTRIBUTOR",
//     toOwnerOptions: [
//       { value: "RESELLER",  label: "Reseller" },
//       { value: "CUSTOMER",  label: "Customer (direct)" },
//     ],
//     badgeColor: "secondary",
//   },
//   RESELLER_SALE: {
//     label:          "Reseller Sale",
//     description:    "Transfer from reseller to customer",
//     requiredOwner:  "RESELLER",
//     toOwnerOptions: [
//       { value: "CUSTOMER", label: "Customer" },
//     ],
//     badgeColor: "success",
//   },
//   RMA_RETURN: {
//     label:          "RMA Return",
//     description:    "Customer returns asset to OEM. Opens the return process.",
//     requiredOwner:  "CUSTOMER",
//     toOwnerOptions: [
//       { value: "OEM", label: "OEM" },
//     ],
//     badgeColor: "warning",
//   },
// };

// // Returns the transfer types valid for the asset's current owner_type
// function getAvailableTransferTypes(ownerType) {
//   return Object.entries(TRANSFER_CONFIG)
//     .filter(([, cfg]) => cfg.requiredOwner === ownerType)
//     .map(([type, cfg]) => ({ type, ...cfg }));
// }

// // ─── Ownership state badge ────────────────────────────────────────────────────

// const OWNERSHIP_COLORS = {
//   OEM_OWNED:           { label: "OEM Owned",         color: "#1d4ed8", bg: "#eff6ff" },
//   OWNED_BY_PARTNER:    { label: "Owned by Partner",  color: "#7c3aed", bg: "#f5f3ff" },
//   OWNED_BY_CUSTOMER:   { label: "Owned by Customer", color: "#065f46", bg: "#ecfdf5" },
//   RMA_RETURNED:        { label: "RMA Returned",      color: "#92400e", bg: "#fff7ed" },
// };

// function OwnerBadge({ state }) {
//   const s = OWNERSHIP_COLORS[state] || { label: state, color: "#374151", bg: "#f3f4f6" };
//   return (
//     <Chip label={s.label} size="small"
//       sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, borderRadius: "6px", fontSize: 11 }} />
//   );
// }

// // ─── Result after state maps ──────────────────────────────────────────────────

// const RESULT_STATE_MAP = {
//   OEM_SALE:         "OWNED_BY_CUSTOMER",
//   DISTRIBUTOR_SALE: "OWNED_BY_PARTNER",
//   RESELLER_SALE:    "OWNED_BY_CUSTOMER",
//   RMA_RETURN:       "RMA_RETURNED",
// };

// // ─────────────────────────────────────────────────────────────────────────────
// // MAIN COMPONENT
// // ─────────────────────────────────────────────────────────────────────────────

// export default function TransferModal({ open, onClose, onSuccess, asset, token, activeOrg }) {
//   const [step,         setStep]         = useState(0);   // 0 = configure, 1 = confirm, 2 = done
//   const [transferType, setTransferType] = useState("");
//   const [toOwnerType,  setToOwnerType]  = useState("");
//   const [toOwnerOrgId, setToOwnerOrgId] = useState("");
//   const [purchaseOrder,setPoNumber]     = useState("");
//   const [invoiceNumber,setInvoiceNum]   = useState("");
//   const [remarks,      setRemarks]      = useState("");
//   const [errors,       setErrors]       = useState({});
//   const [submitting,   setSubmitting]   = useState(false);
//   const [result,       setResult]       = useState(null);  // { transfer, asset }

//   const availableTypes = asset ? getAvailableTransferTypes(asset.owner_type) : [];
//   const selectedConfig = transferType ? TRANSFER_CONFIG[transferType] : null;

//   // Reset on open / asset change
//   useEffect(() => {
//     if (open) {
//       setStep(0);
//       setTransferType("");
//       setToOwnerType("");
//       setToOwnerOrgId("");
//       setPoNumber("");
//       setInvoiceNum("");
//       setRemarks("");
//       setErrors({});
//       setSubmitting(false);
//       setResult(null);
//       // Auto-select if only one type available
//       if (asset) {
//         const types = getAvailableTransferTypes(asset.owner_type);
//         if (types.length === 1) {
//           setTransferType(types[0].type);
//           if (types[0].toOwnerOptions.length === 1) {
//             setToOwnerType(types[0].toOwnerOptions[0].value);
//           }
//         }
//       }
//     }
//   }, [open, asset]);

//   // Auto-set toOwnerType when transferType changes and only one option
//   useEffect(() => {
//     if (selectedConfig?.toOwnerOptions.length === 1) {
//       setToOwnerType(selectedConfig.toOwnerOptions[0].value);
//     } else {
//       setToOwnerType("");
//     }
//   }, [transferType]);

//   const validate = () => {
//     const e = {};
//     if (!transferType)  e.transferType  = "Select a transfer type";
//     if (!toOwnerType)   e.toOwnerType   = "Select destination owner type";
//     if (!toOwnerOrgId.trim()) {
//       e.toOwnerOrgId = "Destination organisation ID is required";
//     } else {
//       // Basic UUID format check
//       const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
//       if (!uuidRe.test(toOwnerOrgId.trim())) {
//         e.toOwnerOrgId = "Must be a valid UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)";
//       }
//     }
//     return e;
//   };

//   const handleContinue = () => {
//     const e = validate();
//     if (Object.keys(e).length) { setErrors(e); return; }
//     setErrors({});
//     setStep(1);
//   };

//   const handleSubmit = async () => {
//     setSubmitting(true);
//     try {
//       const res = await axios.post(`${API_BASE}/transfers`, {
//         inventory_asset_id: asset.id,
//         transfer_type:      transferType,
//         to_owner_type:      toOwnerType,
//         to_owner_org_id:    toOwnerOrgId.trim(),
//         purchase_order:     purchaseOrder.trim()  || undefined,
//         invoice_number:     invoiceNumber.trim()  || undefined,
//         remarks:            remarks.trim()        || undefined,
//       }, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//           ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
//         },
//       });

//       setResult(res.data);
//       setStep(2);
//       onSuccess(res.data.transfer, res.data.asset);
//     } catch (err) {
//       const msg = err.response?.data?.error || "Transfer failed — please try again";
//       setErrors({ _submit: msg });
//       setSubmitting(false);
//     }
//   };

//   if (!asset) return null;

//   return (
//     <Dialog open={open} onClose={step === 2 ? onClose : undefined}
//       maxWidth="sm" fullWidth
//       PaperProps={{ sx: { borderRadius: 3 } }}>

//       {/* Header */}
//       <DialogTitle sx={{ pb: 0 }}>
//         <Box display="flex" justifyContent="space-between" alignItems="flex-start">
//           <Box>
//             <Typography variant="h6" fontWeight={700}>Transfer ownership</Typography>
//             <Typography variant="body2" color="text.secondary" mt={0.3}>
//               {asset.serial_number} · {asset.asset_type}
//             </Typography>
//           </Box>
//           <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
//             <CloseIcon fontSize="small" />
//           </IconButton>
//         </Box>

//         {/* Stepper */}
//         {step < 2 && (
//           <Stepper activeStep={step} sx={{ mt: 2, mb: 0.5 }} alternativeLabel>
//             {["Configure", "Confirm"].map(label => (
//               <Step key={label}><StepLabel>{label}</StepLabel></Step>
//             ))}
//           </Stepper>
//         )}
//       </DialogTitle>

//       {/* ── Step 0: Configure ── */}
//       {step === 0 && (
//         <>
//           <DialogContent dividers sx={{ pt: 2.5 }}>
//             {/* Current state summary */}
//             <Box p={2} mb={2.5} borderRadius={2}
//               sx={{ bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
//               <Typography variant="caption" color="text.secondary" fontWeight={700}
//                 display="block" mb={1} textTransform="uppercase" letterSpacing={0.5}>
//                 Current ownership
//               </Typography>
//               <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
//                 <Chip label={asset.owner_type} size="small" variant="outlined"
//                   sx={{ fontWeight: 600, fontSize: 12 }} />
//                 <OwnerBadge state={asset.ownership_state} />
//               </Box>
//             </Box>

//             {/* No valid transfer types — guard */}
//             {availableTypes.length === 0 && (
//               <Alert severity="warning" sx={{ borderRadius: 2 }}>
//                 No transfers available for an asset with owner type "{asset.owner_type}".
//               </Alert>
//             )}

//             {/* Transfer type */}
//             {availableTypes.length > 0 && (
//               <Stack spacing={2.5}>
//                 <TextField select label="Transfer type" size="small" fullWidth
//                   value={transferType}
//                   onChange={e => { setTransferType(e.target.value); setErrors({}); }}
//                   error={!!errors.transferType} helperText={errors.transferType}>
//                   {availableTypes.map(t => (
//                     <MenuItem key={t.type} value={t.type}>
//                       <Box>
//                         <Typography variant="body2" fontWeight={600}>{t.label}</Typography>
//                         <Typography variant="caption" color="text.secondary">{t.description}</Typography>
//                       </Box>
//                     </MenuItem>
//                   ))}
//                 </TextField>

//                 {/* To owner type */}
//                 {selectedConfig && (
//                   <TextField select label="Destination owner type" size="small" fullWidth
//                     value={toOwnerType}
//                     onChange={e => { setToOwnerType(e.target.value); setErrors(e2 => ({ ...e2, toOwnerType: "" })); }}
//                     error={!!errors.toOwnerType} helperText={errors.toOwnerType}
//                     disabled={selectedConfig.toOwnerOptions.length === 1}>
//                     {selectedConfig.toOwnerOptions.map(o => (
//                       <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
//                     ))}
//                   </TextField>
//                 )}

//                 {/* Destination org ID */}
//                 {selectedConfig && (
//                   <TextField label="Destination organisation ID" size="small" fullWidth
//                     placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
//                     value={toOwnerOrgId}
//                     onChange={e => { setToOwnerOrgId(e.target.value); setErrors(e2 => ({ ...e2, toOwnerOrgId: "" })); }}
//                     error={!!errors.toOwnerOrgId} helperText={errors.toOwnerOrgId ||
//                       "Paste the UUID of the receiving organisation from your org management page"}
//                     inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }} />
//                 )}

//                 {selectedConfig && (
//                   <>
//                     <Divider />
//                     <Typography variant="overline" color="text.secondary" fontWeight={700}>
//                       Financial reference (optional)
//                     </Typography>
//                     <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
//                       <TextField label="Purchase order" size="small" value={purchaseOrder}
//                         onChange={e => setPoNumber(e.target.value)} placeholder="PO-2024-001" />
//                       <TextField label="Invoice number" size="small" value={invoiceNumber}
//                         onChange={e => setInvoiceNum(e.target.value)} placeholder="INV-2024-001" />
//                     </Box>
//                     <TextField label="Remarks" size="small" fullWidth multiline rows={2}
//                       value={remarks} onChange={e => setRemarks(e.target.value)}
//                       placeholder="Optional notes about this transfer…" />
//                   </>
//                 )}
//               </Stack>
//             )}
//           </DialogContent>

//           <DialogActions sx={{ px: 3, py: 2 }}>
//             <Button onClick={onClose} color="inherit" sx={{ textTransform: "none" }}>Cancel</Button>
//             <Button variant="contained" disableElevation onClick={handleContinue}
//               disabled={availableTypes.length === 0}
//               sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
//               Continue
//             </Button>
//           </DialogActions>
//         </>
//       )}

//       {/* ── Step 1: Confirm ── */}
//       {step === 1 && (
//         <>
//           <DialogContent dividers sx={{ pt: 2.5 }}>
//             {errors._submit && (
//               <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{errors._submit}</Alert>
//             )}

//             <Typography variant="body2" color="text.secondary" mb={2.5}>
//               Please review the transfer details before confirming. This action cannot be undone.
//             </Typography>

//             {/* Summary table */}
//             <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
//               {[
//                 { label: "Asset",            value: `${asset.serial_number} · ${asset.asset_type}` },
//                 { label: "Transfer type",    value: TRANSFER_CONFIG[transferType]?.label },
//                 { label: "From",             value: `${asset.owner_type}` },
//                 { label: "To owner type",    value: toOwnerType },
//                 { label: "To organisation",  value: toOwnerOrgId, mono: true },
//                 { label: "New state",        value: RESULT_STATE_MAP[transferType]?.replace(/_/g, " ") },
//                 ...(purchaseOrder ? [{ label: "Purchase order", value: purchaseOrder }] : []),
//                 ...(invoiceNumber ? [{ label: "Invoice number", value: invoiceNumber }] : []),
//                 ...(remarks       ? [{ label: "Remarks",        value: remarks        }] : []),
//               ].map((row, i) => (
//                 <Box key={i} display="flex" px={2} py={1.2}
//                   sx={{ borderBottom: "1px solid", borderColor: "divider",
//                         "&:last-child": { borderBottom: "none" },
//                         bgcolor: i % 2 === 0 ? "background.default" : "background.paper" }}>
//                   <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
//                     {row.label}
//                   </Typography>
//                   <Typography variant="body2" fontWeight={600}
//                     sx={{ fontFamily: row.mono ? "monospace" : "inherit", fontSize: row.mono ? 12 : "inherit" }}>
//                     {row.value}
//                   </Typography>
//                 </Box>
//               ))}
//             </Box>

//             <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2 }}>
//               Ownership transfer records are immutable. Once confirmed, this cannot be reversed.
//             </Alert>
//           </DialogContent>

//           <DialogActions sx={{ px: 3, py: 2 }}>
//             <Button onClick={() => { setStep(0); setErrors({}); setSubmitting(false); }}
//               color="inherit" sx={{ textTransform: "none" }}>
//               Back
//             </Button>
//             <Button variant="contained" color="warning" disableElevation
//               onClick={handleSubmit} disabled={submitting}
//               startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SwapHorizIcon />}
//               sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, minWidth: 160 }}>
//               {submitting ? "Transferring…" : "Confirm transfer"}
//             </Button>
//           </DialogActions>
//         </>
//       )}

//       {/* ── Step 2: Done ── */}
//       {step === 2 && result && (
//         <>
//           <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
//             <Box textAlign="center" py={2}>
//               <CheckCircleIcon sx={{ fontSize: 52, color: "success.main", mb: 2 }} />
//               <Typography variant="h6" fontWeight={700} gutterBottom>
//                 Transfer completed
//               </Typography>
//               <Typography variant="body2" color="text.secondary" mb={3}>
//                 Ownership of <strong>{asset.serial_number}</strong> has been transferred successfully.
//               </Typography>

//               <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 2, textAlign: "left", mb: 2 }}>
//                 {[
//                   { label: "Transfer ID",  value: result.transfer?.id,              mono: true  },
//                   { label: "New owner",    value: result.asset?.owner_type                       },
//                   { label: "New state",    value: result.asset?.ownership_state?.replace(/_/g, " ") },
//                   { label: "Transferred",  value: result.transfer?.transferred_at
//                       ? new Date(result.transfer.transferred_at).toLocaleString("en-IN") : "—" },
//                 ].map((row, i) => (
//                   <Box key={i} display="flex" justifyContent="space-between" py={0.8}
//                     sx={{ borderBottom: i < 3 ? "1px solid" : "none", borderColor: "divider" }}>
//                     <Typography variant="caption" color="text.secondary">{row.label}</Typography>
//                     <Typography variant="caption" fontWeight={600}
//                       sx={{ fontFamily: row.mono ? "monospace" : "inherit", fontSize: row.mono ? 11 : 12 }}>
//                       {row.value}
//                     </Typography>
//                   </Box>
//                 ))}
//               </Box>
//             </Box>
//           </DialogContent>

//           <DialogActions sx={{ px: 3, py: 2 }}>
//             <Button variant="contained" disableElevation onClick={onClose}
//               sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
//               Close
//             </Button>
//           </DialogActions>
//         </>
//       )}
//     </Dialog>
//   );
// }

/**
 * TransferModal
 *
 * Initiates an ownership transfer for a given asset.
 * Opened from AssetDetail.jsx when the user clicks "Transfer ownership".
 *
 * Props:
 *   open        {boolean}
 *   onClose     {() => void}
 *   onSuccess   {(transfer, updatedAsset) => void}
 *   asset       {object}   — the asset being transferred (must have id, serial_number,
 *                            owner_type, ownership_state)
 *   token       {string}
 *   activeOrg   {object|null}
 *
 * API:
 *   POST /api/v1/inventory/transfers
 *
 * Validation:
 *   - transfer_type must be compatible with asset.owner_type
 *   - to_owner_org_id must be a valid UUID
 *   - purchase_order and invoice_number are optional but trimmed
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

// ─── Transfer type configuration ─────────────────────────────────────────────

const TRANSFER_CONFIG = {
  OEM_SALE: {
    label: "OEM Sale",
    description: "Transfer from OEM to a distributor or direct to customer",
    requiredOwner: "OEM",
    toOwnerOptions: [
      { value: "DISTRIBUTOR", label: "Distributor" },
      { value: "CUSTOMER", label: "Customer (direct)" },
    ],
    badgeColor: "primary",
  },
  DISTRIBUTOR_SALE: {
    label: "Distributor Sale",
    description:
      "Transfer from distributor to a reseller or direct to customer",
    requiredOwner: "DISTRIBUTOR",
    toOwnerOptions: [
      { value: "RESELLER", label: "Reseller" },
      { value: "CUSTOMER", label: "Customer (direct)" },
    ],
    badgeColor: "secondary",
  },
  RESELLER_SALE: {
    label: "Reseller Sale",
    description: "Transfer from reseller to customer",
    requiredOwner: "RESELLER",
    toOwnerOptions: [{ value: "CUSTOMER", label: "Customer" }],
    badgeColor: "success",
  },
  RMA_RETURN: {
    label: "RMA Return",
    description: "Customer returns asset to OEM. Opens the return process.",
    requiredOwner: "CUSTOMER",
    toOwnerOptions: [{ value: "OEM", label: "OEM" }],
    badgeColor: "warning",
  },
};

// Returns the transfer types valid for the asset's current owner_type
function getAvailableTransferTypes(ownerType) {
  return Object.entries(TRANSFER_CONFIG)
    .filter(([, cfg]) => cfg.requiredOwner === ownerType)
    .map(([type, cfg]) => ({ type, ...cfg }));
}

// ─── Ownership state badge ────────────────────────────────────────────────────

const OWNERSHIP_COLORS = {
  OEM_OWNED: { label: "OEM Owned", color: "#1d4ed8", bg: "#eff6ff" },
  OWNED_BY_PARTNER: {
    label: "Owned by Partner",
    color: "#7c3aed",
    bg: "#f5f3ff",
  },
  OWNED_BY_CUSTOMER: {
    label: "Owned by Customer",
    color: "#065f46",
    bg: "#ecfdf5",
  },
  RMA_RETURNED: { label: "RMA Returned", color: "#92400e", bg: "#fff7ed" },
};

function OwnerBadge({ state }) {
  const s = OWNERSHIP_COLORS[state] || {
    label: state,
    color: "#374151",
    bg: "#f3f4f6",
  };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        bgcolor: s.bg,
        color: s.color,
        fontWeight: 600,
        borderRadius: "6px",
        fontSize: 11,
      }}
    />
  );
}

// ─── Result after state — dynamic by (transfer_type, to_owner_type) ─────────────
// OEM_SALE to DISTRIBUTOR → OWNED_BY_PARTNER (not OWNED_BY_CUSTOMER)
// OEM_SALE to CUSTOMER    → OWNED_BY_CUSTOMER
// Same fix applied on the backend in get_ownership_state()

const RESULT_STATE_MAP = {
  "OEM_SALE-DISTRIBUTOR": "OWNED_BY_PARTNER",
  "OEM_SALE-RESELLER": "OWNED_BY_PARTNER",
  "OEM_SALE-CUSTOMER": "OWNED_BY_CUSTOMER",
  "DISTRIBUTOR_SALE-RESELLER": "OWNED_BY_PARTNER",
  "DISTRIBUTOR_SALE-CUSTOMER": "OWNED_BY_CUSTOMER",
  "RESELLER_SALE-CUSTOMER": "OWNED_BY_CUSTOMER",
  "RMA_RETURN-OEM": "RMA_RETURNED",
};

function getResultState(transferType, toOwnerType) {
  return RESULT_STATE_MAP[`${transferType}-${toOwnerType}`] || "—";
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function TransferModal({
  open,
  onClose,
  onSuccess,
  asset,
  token,
  activeOrg,
}) {
  const [step, setStep] = useState(0); // 0 = configure, 1 = confirm, 2 = done
  const [transferType, setTransferType] = useState("");
  const [toOwnerType, setToOwnerType] = useState("");
  const [toOwnerOrgId, setToOwnerOrgId] = useState("");
  const [purchaseOrder, setPoNumber] = useState("");
  const [invoiceNumber, setInvoiceNum] = useState("");
  const [remarks, setRemarks] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { transfer, asset }
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);

  const availableTypes = asset
    ? getAvailableTransferTypes(asset.owner_type)
    : [];
  const selectedConfig = transferType ? TRANSFER_CONFIG[transferType] : null;
  const transferBlocked =
    asset?.status === "DEPLOYED" || Boolean(asset?.active_binding);
  const destinationOrganizations = organizations.filter((org) => {
    const orgType = (org.type || "").toUpperCase();
    return (
      (toOwnerType ? orgType === toOwnerType : true) && org.id !== activeOrg?.id
    );
  });
  const currentOwnerType = (asset?.owner_type || "").toUpperCase();
  const ownerFlowCopy =
    currentOwnerType === "OEM"
      ? "Move OEM stock into the downstream commercial channel."
      : currentOwnerType === "DISTRIBUTOR"
        ? "Sell onward from distributor custody to a reseller or direct customer."
        : currentOwnerType === "RESELLER"
          ? "Complete the final customer handoff from reseller stock."
          : "Transfer this asset to the next valid owner in the chain.";

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    const loadOrganizations = async () => {
      try {
        setOrganizationsLoading(true);
        const res = await axios.get(
          `${APP_API_V1_BASE}/organizations`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
            },
          },
        );
        if (!ignore) {
          setOrganizations(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!ignore) {
          setOrganizations([]);
        }
      } finally {
        if (!ignore) {
          setOrganizationsLoading(false);
        }
      }
    };

    loadOrganizations();
    return () => {
      ignore = true;
    };
  }, [open, token, activeOrg]);

  // Reset on open / asset change
  useEffect(() => {
    if (open) {
      setStep(0);
      setTransferType("");
      setToOwnerType("");
      setToOwnerOrgId("");
      setPoNumber("");
      setInvoiceNum("");
      setRemarks("");
      setErrors({});
      setSubmitting(false);
      setResult(null);
      // Auto-select if only one type available
      if (asset) {
        const types = getAvailableTransferTypes(asset.owner_type);
        if (types.length === 1) {
          setTransferType(types[0].type);
          if (types[0].toOwnerOptions.length === 1) {
            setToOwnerType(types[0].toOwnerOptions[0].value);
          }
        }
      }
    }
  }, [open, asset]);

  // Auto-set toOwnerType when transferType changes and only one option
  useEffect(() => {
    if (selectedConfig?.toOwnerOptions.length === 1) {
      setToOwnerType(selectedConfig.toOwnerOptions[0].value);
    } else {
      setToOwnerType("");
    }
    setToOwnerOrgId("");
  }, [transferType]);

  const validate = () => {
    const e = {};
    if (!transferType) e.transferType = "Select a transfer type";
    if (!toOwnerType) e.toOwnerType = "Select destination owner type";
    if (!toOwnerOrgId.trim()) {
      e.toOwnerOrgId = "Select destination organization";
    }
    return e;
  };

  const handleContinue = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setErrors({});
    setStep(1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_BASE}/transfers`,
        {
          inventory_asset_id: asset.id,
          transfer_type: transferType,
          to_owner_type: toOwnerType,
          to_owner_org_id: toOwnerOrgId.trim(),
          purchase_order: purchaseOrder.trim() || undefined,
          invoice_number: invoiceNumber.trim() || undefined,
          remarks: remarks.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
          },
        },
      );

      setResult(res.data);
      setStep(2);
      onSuccess(res.data.transfer, res.data.asset);
    } catch (err) {
      const msg =
        err.response?.data?.error || "Transfer failed — please try again";
      setErrors({ _submit: msg });
      setSubmitting(false);
    }
  };

  if (!asset) return null;

  return (
    <Dialog
      open={open}
      onClose={step === 2 ? onClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 0,
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#e2f8f5" }}>
              Transfer ownership
            </Typography>
            <Typography
              variant="body2"
              mt={0.3}
              sx={{ color: "rgba(203, 213, 225, 0.80)" }}
            >
              {asset.serial_number} · {asset.asset_type}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.8}
            >
              {ownerFlowCopy}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              mt: -0.5,
              color: "rgba(203, 213, 225, 0.80)",
              "&:hover": {
                color: "#e2f8f5",
                background: "rgba(255,255,255,0.08)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Stepper */}
        {step < 2 && (
          <Stepper
            activeStep={step}
            sx={{
              mt: 2,
              mb: 0.5,
              "& .MuiStepLabel-label": {
                color: "rgba(203, 213, 225, 0.70)",
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: "#e2f8f5",
                fontWeight: 600,
              },
              "& .MuiStepLabel-label.Mui-completed": {
                color: "#5eead4",
              },
              "& .MuiStepIcon-root": {
                color: "rgba(148, 163, 184, 0.35)",
              },
              "& .MuiStepIcon-root.Mui-active": {
                color: "#0f766e",
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: "#5eead4",
              },
              "& .MuiStepIcon-text": {
                fill: "#e2f8f5",
              },
              "& .MuiStepConnector-line": {
                borderColor: "rgba(148, 163, 184, 0.25)",
              },
              "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": {
                borderColor: "#0f766e",
              },
              "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
                borderColor: "#5eead4",
              },
            }}
            alternativeLabel
          >
            {["Configure", "Confirm"].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogTitle>

      {/* ── Step 0: Configure ── */}
      {step === 0 && (
        <>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            {/* Current state summary */}
            <Box
              p={2}
              mb={2.5}
              borderRadius={2}
              sx={{
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                display="block"
                mb={1}
                textTransform="uppercase"
                letterSpacing={0.5}
              >
                Current ownership
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <Chip
                  label={asset.owner_type}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: 12 }}
                />
                <OwnerBadge state={asset.ownership_state} />
              </Box>
            </Box>

            {/* No valid transfer types — guard */}
            {availableTypes.length === 0 && (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                No transfers available for an asset with owner type "
                {asset.owner_type}".
              </Alert>
            )}

            {transferBlocked && (
              <Alert severity="warning" sx={{ borderRadius: 2, mb: 2.5 }}>
                This asset is currently deployed or actively bound to a device.
                Unbind or decommission it first, then transfer it as in-stock
                inventory to the receiving organization.
              </Alert>
            )}

            {/* Transfer type */}
            {availableTypes.length > 0 && (
              <Stack spacing={2.5}>
                <TextField
                  select
                  label="Transfer type"
                  size="small"
                  fullWidth
                  value={transferType}
                  onChange={(e) => {
                    setTransferType(e.target.value);
                    setErrors({});
                  }}
                  error={!!errors.transferType}
                  helperText={errors.transferType}
                >
                  {availableTypes.map((t) => (
                    <MenuItem key={t.type} value={t.type}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {t.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>

                {/* To owner type */}
                {selectedConfig && (
                  <TextField
                    select
                    label="Destination owner type"
                    size="small"
                    fullWidth
                    value={toOwnerType}
                    onChange={(e) => {
                      setToOwnerType(e.target.value);
                      setErrors((e2) => ({ ...e2, toOwnerType: "" }));
                    }}
                    error={!!errors.toOwnerType}
                    helperText={errors.toOwnerType}
                    disabled={selectedConfig.toOwnerOptions.length === 1}
                  >
                    {selectedConfig.toOwnerOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {/* Destination org ID */}
                {selectedConfig && (
                  <TextField
                    select
                    label="Destination organization"
                    size="small"
                    fullWidth
                    value={toOwnerOrgId}
                    onChange={(e) => {
                      setToOwnerOrgId(e.target.value);
                      setErrors((e2) => ({ ...e2, toOwnerOrgId: "" }));
                    }}
                    error={!!errors.toOwnerOrgId}
                    helperText={
                      errors.toOwnerOrgId ||
                      (organizationsLoading
                        ? "Loading organizations..."
                        : destinationOrganizations.length === 0
                          ? "No valid destination organizations are available for this owner type."
                          : "Select the receiving organization by name")
                    }
                  >
                    {destinationOrganizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                        {org.code ? ` (${org.code})` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {selectedConfig && (
                  <>
                    <Divider />
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      fontWeight={700}
                    >
                      Financial reference (optional)
                    </Typography>
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                      <TextField
                        label="Purchase order"
                        size="small"
                        value={purchaseOrder}
                        onChange={(e) => setPoNumber(e.target.value)}
                        placeholder="PO-2024-001"
                      />
                      <TextField
                        label="Invoice number"
                        size="small"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNum(e.target.value)}
                        placeholder="INV-2024-001"
                      />
                    </Box>
                    <TextField
                      label="Remarks"
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Optional notes about this transfer…"
                    />
                  </>
                )}
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={onClose}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleContinue}
              disabled={availableTypes.length === 0 || transferBlocked}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Continue
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Step 1: Confirm ── */}
      {step === 1 && (
        <>
          <DialogContent dividers sx={{ pt: 2.5 }}>
            {errors._submit && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {errors._submit}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Please review the transfer details before confirming. This action
              cannot be undone.
            </Typography>

            {/* Summary table */}
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {[
                {
                  label: "Asset",
                  value: `${asset.serial_number} · ${asset.asset_type}`,
                },
                {
                  label: "Transfer type",
                  value: TRANSFER_CONFIG[transferType]?.label,
                },
                { label: "From", value: `${asset.owner_type}` },
                { label: "To owner type", value: toOwnerType },
                {
                  label: "To organisation",
                  value:
                    destinationOrganizations.find(
                      (org) => org.id === toOwnerOrgId,
                    )?.name || toOwnerOrgId,
                },
                {
                  label: "New state",
                  value: getResultState(transferType, toOwnerType).replace(
                    /_/g,
                    " ",
                  ),
                },
                ...(purchaseOrder
                  ? [{ label: "Purchase order", value: purchaseOrder }]
                  : []),
                ...(invoiceNumber
                  ? [{ label: "Invoice number", value: invoiceNumber }]
                  : []),
                ...(remarks ? [{ label: "Remarks", value: remarks }] : []),
              ].map((row, i) => (
                <Box
                  key={i}
                  display="flex"
                  px={2}
                  py={1.2}
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:last-child": { borderBottom: "none" },
                    bgcolor:
                      i % 2 === 0 ? "background.default" : "background.paper",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 160 }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      fontFamily: row.mono ? "monospace" : "inherit",
                      fontSize: row.mono ? 12 : "inherit",
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2 }}>
              Ownership transfer records are immutable. Once confirmed, this
              cannot be reversed.
            </Alert>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setStep(0);
                setErrors({});
                setSubmitting(false);
              }}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              color="warning"
              disableElevation
              onClick={handleSubmit}
              disabled={submitting || transferBlocked}
              startIcon={
                submitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SwapHorizIcon />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                minWidth: 160,
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              }}
            >
              {submitting ? "Transferring…" : "Confirm transfer"}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ── Step 2: Done ── */}
      {step === 2 && result && (
        <>
          <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
            <Box textAlign="center" py={2}>
              <CheckCircleIcon
                sx={{ fontSize: 52, color: "success.main", mb: 2 }}
              />
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Transfer completed
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Ownership of <strong>{asset.serial_number}</strong> has been
                transferred successfully.
              </Typography>

              <Box
                sx={{
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "left",
                  mb: 2,
                }}
              >
                {[
                  {
                    label: "Transfer ID",
                    value: result.transfer?.id,
                    mono: true,
                  },
                  { label: "New owner", value: result.asset?.owner_type },
                  {
                    label: "New state",
                    value: result.asset?.ownership_state?.replace(/_/g, " "),
                  },
                  {
                    label: "Transferred",
                    value: result.transfer?.transferred_at
                      ? new Date(result.transfer.transferred_at).toLocaleString(
                          "en-IN",
                        )
                      : "—",
                  },
                ].map((row, i) => (
                  <Box
                    key={i}
                    display="flex"
                    justifyContent="space-between"
                    py={0.8}
                    sx={{
                      borderBottom: i < 3 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {row.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{
                        fontFamily: row.mono ? "monospace" : "inherit",
                        fontSize: row.mono ? 11 : 12,
                      }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onClose}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
